import { useMemo, useState } from 'react';
import { View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import lodashIsNumber from 'lodash/isNumber';

import Button from '../../../Components/Button';
import DropdownPopover from '../../../Components/DropdownPopover';
import {
  defaultPartySizes,
  textCategory,
} from '../../../Components/Reservation/constants';
import {
  ids as reservationStyleIds,
  styles as reservationStyles,
} from '../../../Components/Reservation/styles';
import ResponsiveText from '../../../Components/ResponsiveText';
import constants from '../../../Config/constants';
import { pluralize } from '../../../Helper';
import useReservationBooking from '../../../Hooks/useReservationBooking';
import routeList from '../../../Routes/list';
import themeStyle from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';

import OptionBox from '../../ReservationStoreDetails/components/OptionBox';

import PreviewNote from '../PreviewNote';

import Overview from './Overview';

type PartySizeProps = {
  eventId: string;
  eventItemId: string;
  isEnded: boolean;
  isMultipleDate?: boolean;
  isSoldOut?: boolean;
  size: number;
  soldOutQty?: number;
  startDate: string;
  onPress: (size: number) => void;
};

const OverviewAndPartySize = (props: PartySizeProps) => {
  const {
    eventId,
    eventItemId,
    isEnded,
    isMultipleDate,
    isSoldOut,
    size,
    soldOutQty,
    startDate,
    onPress,
  } = props;
  const route = useRoute();
  const { isCanContinue } = useReservationBooking();
  const [selectedSize, setSelectedSize] = useState({
    label: `${size} ${pluralize(size, 'guest')}`,
    value: size,
  });

  const isPreview = route?.name === routeList.RES_EVENT_DETAILS_PREVIEW;
  const hasSoldOutQty = lodashIsNumber(soldOutQty);
  const isContinue = isCanContinue({
    itemId: eventItemId,
    date: dayjs(startDate).format(constants.DBDATE_FORMAT),
    time: dayjs(startDate).format(constants.DBTIME_FORMAT),
    size: selectedSize.value,
  });

  const PartySizeTrigger = useMemo(
    () => (
      <OptionBox
        label="Party Size"
        placeholder="Party of"
        value={selectedSize}
      />
    ),
    [selectedSize],
  );

  return (
    <View
      dataSet={{ media: reservationStyleIds.boxContainer }}
      style={reservationStyles.boxContainer}
    >
      <View
        dataSet={{ media: platformStyleIds.webOnly }}
        style={platformStyles.webOnly}
      >
        <View
          dataSet={{ media: reservationStyleIds.box }}
          style={[reservationStyles.box, themeStyle.spacingBottomMedium]}
        >
          <Overview eventId={eventId} />
        </View>
      </View>
      {isPreview ? (
        <PreviewNote />
      ) : (
        <View
          dataSet={{ media: reservationStyleIds.box }}
          style={reservationStyles.box}
        >
          {(isSoldOut && soldOutQty <= 1) || isEnded ? (
            <ResponsiveText
              appearance="hint"
              category={textCategory.BODY}
              style={themeStyle.textCenter}
            >
              {isSoldOut ? 'Sold out!' : 'Event is no longer available.'}
            </ResponsiveText>
          ) : (
            <View
              dataSet={{ media: ids.partySizeWrapper }}
              style={styles.partySizeWrapper}
            >
              <View
                dataSet={{ media: ids.partySizeContainer }}
                style={styles.partySizeContainer}
              >
                <DropdownPopover
                  trigger={PartySizeTrigger}
                  value={selectedSize}
                  data={defaultPartySizes}
                  selectionType="color"
                  onSelect={setSelectedSize}
                />
              </View>
              <View
                dataSet={{ media: ids.buttonContainer }}
                style={styles.buttonContainer}
              >
                <Button
                  disabled={
                    hasSoldOutQty ? selectedSize.value >= soldOutQty : false
                  }
                  status="info"
                  style={[
                    themeStyle.pageVerticalSpacingMedium,
                    themeStyle.noMinHeight,
                  ]}
                  onPress={() => onPress(selectedSize.value)}
                >
                  {() => {
                    let text = 'Get tickets';
                    if (isMultipleDate) {
                      text = 'Choose a date';
                    } else if (isContinue) {
                      text = 'Continue';
                    }
                    return (
                      <ResponsiveText
                        category={textCategory.BODY}
                        appearance="alternative"
                      >
                        {text}
                      </ResponsiveText>
                    );
                  }}
                </Button>
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const { ids, styles } = StyleSheet.create({
  partySizeWrapper: {
    ...themeStyle.flex1,
    ...themeStyle.flexDirectionRow,
    ...themeStyle.alignItemsEnd,
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.flexDirectionCol,
      alignSelf: 'stretch',
    },
  },
  partySizeContainer: {
    ...themeStyle.flex1,
    ...themeStyle.spacingRightMedium,
    [`@media ${breakpoints.lg}`]: {
      ...themeStyle.spacingBottomMedium,
      marginRight: 0,
      alignSelf: 'stretch',
    },
  },
  buttonContainer: {
    ...themeStyle.flex1,
    [`@media ${breakpoints.lg}`]: {
      marginBottom: 0,
      alignSelf: 'stretch',
    },
  },
});

export default OverviewAndPartySize;
